import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import OrderService from '../service/order.service';
import PrintService from '../service/print.service';
import { formatCentsToBRL } from '../utils/currency';
import {
  isMonitoredStatus,
  isOrderStale,
  getOrderAgeMinutes,
  STALE_REPEAT_MS,
  STATUS_LABELS,
} from '../utils/orderAlertUtils';
import { playNewOrderSound, playStaleOrderSound } from '../hooks/useOrderAlertSound';
import {
  ensureNotificationPermission,
  notifyNewOrder,
  notifyNewOrdersGrouped,
} from '../utils/browserNotification';
import { ApiContext } from './ApiContext';

const OrderAlertsContext = createContext(null);

const SOUND_MUTED_KEY = 'orderAlerts.soundMuted';
const STALE_CHECK_INTERVAL_MS = 60 * 1000;
const NEW_ORDER_GROUP_WINDOW_MS = 30 * 1000;
const MAX_VISIBLE_TOASTS = 2;
const NEW_TOAST_DURATION = 6000;
const STALE_TOAST_DURATION = 12000;
const PRINT_OFFLINE_REPEAT_MS = 5 * 60 * 1000;
const PRINT_CHECK_INTERVAL_MS = 60 * 1000;

const toOrderSnapshot = (order) => ({
  id: order.id,
  status: order.status,
  customerName: order.customerName,
  totalCents: order.totalCents,
  createdAt: order.createdAt || order.created_at,
  updatedAt: order.updatedAt || order.updated_at,
  resolvedAt: order.resolvedAt || order.resolved_at,
});

let alertIdCounter = 0;
const nextAlertId = () => {
  alertIdCounter += 1;
  return `alert-${alertIdCounter}-${Date.now()}`;
};

const flashDocumentTitle = (message) => {
  if (typeof document === 'undefined' || !document.hidden) return;
  const original = document.title;
  let ticks = 0;
  const interval = setInterval(() => {
    document.title = ticks % 2 === 0 ? message : original;
    ticks += 1;
    if (ticks >= 6) {
      clearInterval(interval);
      document.title = original;
    }
  }, 1000);
};

export const OrderAlertsProvider = ({ accountId, children }) => {
  const location = useLocation();
  const { account, setAccount } = useContext(ApiContext);
  const [ordersMap, setOrdersMap] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [soundMuted, setSoundMuted] = useState(() => {
    try {
      return localStorage.getItem(SOUND_MUTED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const ordersMapRef = useRef({});
  const subscribersRef = useRef(new Set());
  const staleAlertedRef = useRef({});
  const recentNewOrdersRef = useRef([]);
  const dismissTimersRef = useRef({});
  const soundMutedRef = useRef(soundMuted);
  const lastPrintOfflineAlertRef = useRef(0);

  useEffect(() => {
    soundMutedRef.current = soundMuted;
  }, [soundMuted]);

  const notifySubscribers = useCallback((type, order) => {
    subscribersRef.current.forEach((fn) => {
      try {
        fn(type, order);
      } catch (e) {
        // ignore subscriber errors
      }
    });
  }, []);

  const subscribeOrderEvents = useCallback((callback) => {
    subscribersRef.current.add(callback);
    return () => subscribersRef.current.delete(callback);
  }, []);

  const syncOrdersMap = useCallback((updater) => {
    setOrdersMap((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      ordersMapRef.current = next;
      return next;
    });
  }, []);

  const dismissAlertById = useCallback((id) => {
    if (dismissTimersRef.current[id]) {
      clearTimeout(dismissTimersRef.current[id]);
      delete dismissTimersRef.current[id];
    }
    setAlerts((prev) => {
      const remaining = prev.filter((a) => a.id !== id);
      const visible = remaining.filter((a) => a.visible);
      const queued = remaining.filter((a) => !a.visible);
      const slots = MAX_VISIBLE_TOASTS - visible.length;
      if (slots > 0 && queued.length > 0) {
        const toShow = queued.slice(0, slots);
        const stillQueued = queued.slice(slots);
        const promoted = toShow.map((a) => ({ ...a, visible: true }));
        promoted.forEach((a) => {
        const duration = a.type === 'staleOrder' || a.type === 'printOffline'
          ? STALE_TOAST_DURATION
          : NEW_TOAST_DURATION;
          dismissTimersRef.current[a.id] = setTimeout(() => {
            dismissAlertById(a.id);
          }, duration);
        });
        return [...visible, ...promoted, ...stillQueued];
      }
      return remaining;
    });
  }, []);

  const scheduleDismiss = useCallback((alertId, type) => {
    const duration = type === 'staleOrder' || type === 'printOffline'
      ? STALE_TOAST_DURATION
      : NEW_TOAST_DURATION;
    dismissTimersRef.current[alertId] = setTimeout(() => {
      dismissAlertById(alertId);
    }, duration);
  }, [dismissAlertById]);

  const pushAlert = useCallback((alert, { playSound, soundType }) => {
    setAlerts((prev) => {
      const visibleCount = prev.filter((a) => a.visible).length;
      const entry = { ...alert, visible: visibleCount < MAX_VISIBLE_TOASTS };
      if (entry.visible) scheduleDismiss(entry.id, entry.type);
      return [...prev, entry];
    });

    if (playSound && !soundMutedRef.current) {
      if (soundType === 'stale') playStaleOrderSound();
      else playNewOrderSound();
    }
  }, [scheduleDismiss]);

  const isOnOrdersPage = /\/pedidos(?:\/|$)/.test(location.pathname) &&
    !location.pathname.includes('/novo') &&
    !location.pathname.includes('/editar');

  const triggerNewOrderAlert = useCallback((order) => {
    const now = Date.now();
    recentNewOrdersRef.current = [
      ...recentNewOrdersRef.current.filter((o) => now - o.at < NEW_ORDER_GROUP_WINDOW_MS),
      { id: order.id, at: now },
    ];

    const shouldSkipToast = isOnOrdersPage;
    const grouped = recentNewOrdersRef.current.length >= 3;

    if (grouped) {
      notifyNewOrdersGrouped(recentNewOrdersRef.current.length);
      if (!shouldSkipToast) {
        setAlerts((prev) => {
          const existing = prev.find((a) => a.type === 'newOrderGrouped');
          if (existing) {
            return prev.map((a) =>
              a.type === 'newOrderGrouped'
                ? {
                    ...a,
                    count: recentNewOrdersRef.current.length,
                    message: `${recentNewOrdersRef.current.length} novos pedidos`,
                  }
                : a
            );
          }
          const visibleCount = prev.filter((a) => a.visible).length;
          const entry = {
            id: nextAlertId(),
            type: 'newOrderGrouped',
            count: recentNewOrdersRef.current.length,
            message: `${recentNewOrdersRef.current.length} novos pedidos`,
            visible: visibleCount < MAX_VISIBLE_TOASTS,
          };
          if (entry.visible) scheduleDismiss(entry.id, entry.type);
          return [...prev, entry];
        });
      }
      if (!soundMutedRef.current) playNewOrderSound();
      flashDocumentTitle(`${recentNewOrdersRef.current.length} novos pedidos!`);
      return;
    }

    if (shouldSkipToast) {
      notifyNewOrder(order.id, order.customerName, order.totalCents);
      if (!soundMutedRef.current) playNewOrderSound();
      flashDocumentTitle(`Novo pedido #${order.id}!`);
      return;
    }

    notifyNewOrder(order.id, order.customerName, order.totalCents);

    pushAlert({
      id: nextAlertId(),
      type: 'newOrder',
      orderId: order.id,
      message: `Novo pedido #${order.id} — ${order.customerName} · ${formatCentsToBRL(order.totalCents)}`,
    }, { playSound: true, soundType: 'new' });
    flashDocumentTitle(`Novo pedido #${order.id}!`);
  }, [isOnOrdersPage, pushAlert, scheduleDismiss]);

  const triggerStatusChangeAlert = useCallback((order, previousStatus) => {
    const label = STATUS_LABELS[order.status] || order.status;
    const prevLabel = STATUS_LABELS[previousStatus] || previousStatus;

    pushAlert({
      id: nextAlertId(),
      type: 'statusChange',
      orderId: order.id,
      message: `Pedido #${order.id} — ${prevLabel} → ${label}`,
    }, { playSound: true, soundType: 'new' });

    flashDocumentTitle(`Pedido #${order.id}: ${label}`);
  }, [pushAlert]);

  const triggerStaleAlert = useCallback((order) => {
    const ageMin = getOrderAgeMinutes(order);
    const label = STATUS_LABELS[order.status] || order.status;
    pushAlert({
      id: nextAlertId(),
      type: 'staleOrder',
      orderId: order.id,
      message: `Pedido #${order.id} parado há ${ageMin} min — ${label}`,
    }, { playSound: true, soundType: 'stale' });
    flashDocumentTitle(`Pedido #${order.id} aguardando!`);
    staleAlertedRef.current[order.id] = Date.now();
  }, [pushAlert]);

  const checkStaleOrders = useCallback(() => {
    const map = ordersMapRef.current;
    Object.values(map).forEach((order) => {
      if (!isOrderStale(order)) {
        delete staleAlertedRef.current[order.id];
        return;
      }
      const lastAlert = staleAlertedRef.current[order.id] || 0;
      if (Date.now() - lastAlert >= STALE_REPEAT_MS) {
        triggerStaleAlert(order);
      }
    });
  }, [triggerStaleAlert]);

  const upsertOrder = useCallback((order, { isNew = false } = {}) => {
    if (!order || !order.id) return;
    if (order.resolvedAt || order.resolved_at) {
      syncOrdersMap((prev) => {
        const next = { ...prev };
        delete next[order.id];
        return next;
      });
      delete staleAlertedRef.current[order.id];
      return;
    }

    syncOrdersMap((prev) => ({
      ...prev,
      [order.id]: toOrderSnapshot(order),
    }));

    if (isNew) {
      triggerNewOrderAlert(order);
    }
  }, [syncOrdersMap, triggerNewOrderAlert]);

  useEffect(() => {
    if (!accountId) return undefined;
    let cancelled = false;

    OrderService.fetchOrders({ accountId, page: 1, size: 30 })
      .then((res) => {
        if (cancelled) return;
        const map = {};
        (res.data || []).forEach((order) => {
          if (!order.resolvedAt && !order.resolved_at) {
            map[order.id] = toOrderSnapshot(order);
            if (isOrderStale(order)) {
              staleAlertedRef.current[order.id] = Date.now();
            }
          }
        });
        syncOrdersMap(map);
      })
      .catch((err) => console.error('OrderAlerts: failed to load orders', err));

    return () => { cancelled = true; };
  }, [accountId, syncOrdersMap]);

  useEffect(() => {
    if (!accountId) return undefined;

    const apiBase =
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_API_BASE_URL ||
      'http://localhost:3001';
    const socketUrl = `${apiBase.replace(/\/$/, '')}/orders`;
    const socket = io(socketUrl, {
      auth: { token: localStorage.getItem('token') },
      query: { accountId: String(accountId) },
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    const onConnect = () => {
      console.log('[OrderAlerts] WebSocket connected:', socketUrl, 'accountId:', accountId);
      socket.emit('join', { accountId: Number(accountId) });
    };

    const onConnectError = (err) => {
      console.error('[OrderAlerts] WebSocket connect_error:', err?.message || err);
    };

    const onDisconnect = (reason) => {
      console.warn('[OrderAlerts] WebSocket disconnected:', reason);
    };

    const onCreated = (order) => {
      if (!order) return;
      notifySubscribers('created', order);
      upsertOrder(order, { isNew: true });
    };

    const onUpdated = (order) => {
      if (!order) return;
      const previous = ordersMapRef.current[order.id];
      notifySubscribers('updated', order);
      upsertOrder(order, { isNew: false });

      if (previous && previous.status !== order.status) {
        triggerStatusChangeAlert(order, previous.status);
      }
    };

    const onModulesUpdated = (modules) => {
      if (!modules) return;
      setAccount((prev) => (prev && prev.id === Number(accountId) ? { ...prev, ...modules } : prev));
    };

    socket.on('connect', onConnect);
    socket.on('connect_error', onConnectError);
    socket.on('disconnect', onDisconnect);
    socket.on('order.created', onCreated);
    socket.on('order.updated', onUpdated);
    socket.on('account.modules.updated', onModulesUpdated);

    return () => {
      socket.off('connect', onConnect);
      socket.off('connect_error', onConnectError);
      socket.off('disconnect', onDisconnect);
      socket.off('order.created', onCreated);
      socket.off('order.updated', onUpdated);
      socket.off('account.modules.updated', onModulesUpdated);
      try {
        socket.disconnect();
      } catch (e) {
        // ignore
      }
    };
  }, [accountId, notifySubscribers, upsertOrder, triggerStatusChangeAlert, setAccount]);

  useEffect(() => {
    if (!accountId) return undefined;
    ensureNotificationPermission().catch(() => {});
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return undefined;
    checkStaleOrders();
    const interval = setInterval(checkStaleOrders, STALE_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [accountId, checkStaleOrders]);

  useEffect(() => {
    if (account?.printingEnabled) return;
    lastPrintOfflineAlertRef.current = 0;
    setAlerts((prev) => {
      const printAlerts = prev.filter((a) => a.type === 'printOffline');
      if (!printAlerts.length) return prev;
      printAlerts.forEach((a) => {
        clearTimeout(dismissTimersRef.current[a.id]);
        delete dismissTimersRef.current[a.id];
      });
      return prev.filter((a) => a.type !== 'printOffline');
    });
  }, [account?.printingEnabled]);

  useEffect(() => {
    if (!accountId || !account?.printingEnabled) return undefined;

    const checkPrintAgent = async () => {
      try {
        const status = await PrintService.getStatus(accountId);
        if (status?.online) {
          lastPrintOfflineAlertRef.current = 0;
          return;
        }
        const now = Date.now();
        if (
          lastPrintOfflineAlertRef.current &&
          now - lastPrintOfflineAlertRef.current < PRINT_OFFLINE_REPEAT_MS
        ) {
          return;
        }
        lastPrintOfflineAlertRef.current = now;
        pushAlert(
          {
            id: nextAlertId(),
            type: 'printOffline',
            message: 'PedeGo Impressões desconectado — notas não serão impressas',
            createdAt: now,
          },
          { playSound: true, soundType: 'stale' },
        );
        flashDocumentTitle('Impressão offline');
      } catch {
        // ignore
      }
    };

    checkPrintAgent();
    const interval = setInterval(checkPrintAgent, PRINT_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [accountId, account?.printingEnabled, pushAlert]);

  useEffect(() => () => {
    Object.values(dismissTimersRef.current).forEach(clearTimeout);
  }, []);

  const toggleSoundMuted = useCallback(() => {
    setSoundMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SOUND_MUTED_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const dismissAlert = useCallback((id) => {
    dismissAlertById(id);
  }, [dismissAlertById]);

  const { pendingCount, staleCount } = useMemo(() => {
    const orders = Object.values(ordersMap);
    const pending = orders.filter((o) => isMonitoredStatus(o.status)).length;
    const stale = orders.filter((o) => isOrderStale(o)).length;
    return { pendingCount: pending, staleCount: stale };
  }, [ordersMap]);

  const value = useMemo(() => ({
    alerts,
    pendingCount,
    staleCount,
    soundMuted,
    toggleSoundMuted,
    dismissAlert,
    subscribeOrderEvents,
    isOrderStale,
    getOrderAgeMinutes,
  }), [
    alerts,
    pendingCount,
    staleCount,
    soundMuted,
    toggleSoundMuted,
    dismissAlert,
    subscribeOrderEvents,
  ]);

  return (
    <OrderAlertsContext.Provider value={value}>
      {children}
    </OrderAlertsContext.Provider>
  );
};

export const useOrderAlerts = () => {
  const ctx = useContext(OrderAlertsContext);
  if (!ctx) {
    return {
      alerts: [],
      pendingCount: 0,
      staleCount: 0,
      soundMuted: false,
      toggleSoundMuted: () => {},
      dismissAlert: () => {},
      subscribeOrderEvents: () => () => {},
      isOrderStale: () => false,
      getOrderAgeMinutes: () => 0,
    };
  }
  return ctx;
};

export default OrderAlertsContext;
